/**
 * ============================================================================
 * 📐 RegionSelector — Empfänger-Bereich auf dem Originalbild korrigieren
 * ============================================================================
 *
 * Zeigt das Originalbild im Fullscreen-Overlay mit einem verschiebbaren/
 * vergrößerbaren Rahmen. Der User kann den Empfänger-Bereich markieren,
 * dann wird OCR auf dem korrigierten Bereich neu ausgeführt.
 *
 * Props:
 *   imageUrl     — Data-URL des Originalbildes
 *   initialBox   — { x, y, w, h } in Original-Pixel-Koordinaten
 *   imageSize    — { w, h } Original-Bildgröße in Pixel
 *   onConfirm    — (box: {x,y,w,h}) => void  (Original-Koordinaten)
 *   onCancel     — () => void
 *   colors       — Theme-Farben
 *   loading      — Lade-Status (während Re-Analyse)
 */

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../hooks/useTranslation';

const HANDLE_RADIUS = 18;   // Touch-freundliche Handle-Größe
const MIN_BOX_SIZE = 20;    // Minimale Auswahl-Größe in Display-Pixel (reduziert für bessere Mobile-Unterstützung)
const MIN_ORIGINAL_SIZE = 50; // Minimale Größe in Original-Pixeln
const ACCENT = '#00aaff';

// Farben und Labels für alle Regionstypen
const REGION_COLORS = {
  empfaenger: { color: '#00aaff', labelKey: 'ocr.regionRecipient', label: 'Empfänger' },
  absender: { color: '#ff9800', labelKey: 'ocr.regionSender', label: 'Absender' },
  barcode: { color: '#9c27b0', labelKey: 'ocr.regionBarcode', label: 'Barcode' },
  tracking: { color: '#e91e63', labelKey: 'ocr.regionTracking', label: 'Tracking-Nr.' },
  label: { color: '#607d8b', labelKey: 'ocr.regionLabel', label: 'Label' },
  gewicht: { color: '#4caf50', labelKey: 'ocr.regionWeight', label: 'Gewicht' },
};

export default function RegionSelector({ 
  imageUrl, initialBox, imageSize, onConfirm, onCancel, colors, loading, allRegions 
}) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [box, setBox] = useState(null);           // Display-Koordinaten
  const [scale, setScale] = useState({ x: 1, y: 1 });
  const [imgLoaded, setImgLoaded] = useState(false);
  const interactionRef = useRef(null);

  // ── Bild geladen → Scale berechnen + initialen Rahmen positionieren ──
  const handleImageLoad = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;

    const dw = img.clientWidth;
    const dh = img.clientHeight;
    
    // Fallback: imageSize aus naturalWidth/naturalHeight
    const natW = imageSize?.width || imageSize?.w || img.naturalWidth;
    const natH = imageSize?.height || imageSize?.h || img.naturalHeight;
    
    const sx = natW / dw;
    const sy = natH / dh;
    setScale({ x: sx, y: sy });

    if (initialBox) {
      setBox({
        x: initialBox.x / sx,
        y: initialBox.y / sy,
        w: initialBox.w / sx,
        h: initialBox.h / sy,
      });
    } else {
      // Standard-Box: Mittlerer Bereich
      setBox({
        x: dw * 0.1,
        y: dh * 0.25,
        w: dw * 0.8,
        h: dh * 0.5,
      });
    }

    setImgLoaded(true);
  }, [imageSize, initialBox]);

  // ── Display → Original Pixel ──
  const toOriginal = useCallback((b) => ({
    x: Math.round(b.x * scale.x),
    y: Math.round(b.y * scale.y),
    w: Math.round(b.w * scale.x),
    h: Math.round(b.h * scale.y),
  }), [scale]);

  // ── Pointer-Position relativ zum Container ──
  const getPos = useCallback((e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const cx = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const cy = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    return { x: cx - rect.left, y: cy - rect.top };
  }, []);

  // ── Hit-Test: Was tut der User? ──
  const hitTest = useCallback((pos) => {
    if (!box) return { action: 'new' };

    // Ecken prüfen (vergrößerter Trefferbereich für Touch)
    const corners = [
      { name: 'nw', x: box.x,         y: box.y },
      { name: 'ne', x: box.x + box.w, y: box.y },
      { name: 'sw', x: box.x,         y: box.y + box.h },
      { name: 'se', x: box.x + box.w, y: box.y + box.h },
    ];

    for (const c of corners) {
      if (Math.abs(pos.x - c.x) < HANDLE_RADIUS * 1.8 && 
          Math.abs(pos.y - c.y) < HANDLE_RADIUS * 1.8) {
        return { action: 'resize', handle: c.name };
      }
    }

    // Kanten prüfen (Top, Bottom, Left, Right)
    const edgeThreshold = HANDLE_RADIUS;
    if (pos.x >= box.x && pos.x <= box.x + box.w) {
      if (Math.abs(pos.y - box.y) < edgeThreshold) return { action: 'resize', handle: 'n' };
      if (Math.abs(pos.y - (box.y + box.h)) < edgeThreshold) return { action: 'resize', handle: 's' };
    }
    if (pos.y >= box.y && pos.y <= box.y + box.h) {
      if (Math.abs(pos.x - box.x) < edgeThreshold) return { action: 'resize', handle: 'w' };
      if (Math.abs(pos.x - (box.x + box.w)) < edgeThreshold) return { action: 'resize', handle: 'e' };
    }

    // Innerhalb der Box = Verschieben
    if (pos.x >= box.x && pos.x <= box.x + box.w && 
        pos.y >= box.y && pos.y <= box.y + box.h) {
      return { action: 'move' };
    }

    return { action: 'new' };
  }, [box]);

  // ── Box im Bild halten ──
  const clampBox = useCallback((b) => {
    const img = imgRef.current;
    if (!img) return b;
    const maxW = img.clientWidth;
    const maxH = img.clientHeight;

    let { x, y, w, h } = b;
    
    // Minimale Größe
    w = Math.max(MIN_BOX_SIZE, w);
    h = Math.max(MIN_BOX_SIZE, h);
    
    // Nicht über Bildrand hinaus
    w = Math.min(w, maxW);
    h = Math.min(h, maxH);
    x = Math.max(0, Math.min(x, maxW - w));
    y = Math.max(0, Math.min(y, maxH - h));

    return { x, y, w, h };
  }, []);

  // ── Pointer-Events (Document-Level statt Pointer Capture) ──
  // Pointer Capture kann Klicks auf Buttons blockieren, daher verwenden wir
  // document-level Event-Listener für Drag-Operationen.
  
  const handlePointerDown = useCallback((e) => {
    if (loading) return;
    // Buttons nicht abfangen
    if (e.target.closest && e.target.closest('button')) return;
    if (e.target.tagName === 'BUTTON') return;
    e.preventDefault();
    
    const pos = getPos(e);
    const hit = hitTest(pos);

    const inter = {
      action: hit.action,
      handle: hit.handle,
      startPos: pos,
      startBox: box ? { ...box } : null,
    };

    if (hit.action === 'new') {
      const newBox = { x: pos.x, y: pos.y, w: 1, h: 1 };
      setBox(newBox);
      inter.startBox = newBox;
      inter.anchorPos = pos;
    }

    interactionRef.current = inter;
    // KEIN setPointerCapture — das blockiert Klicks auf andere Elemente!
  }, [box, getPos, hitTest, loading]);

  // Document-Level Move/Up Listener (reagiert auch außerhalb des Containers)
  useEffect(() => {
    const handleDocMove = (e) => {
      const inter = interactionRef.current;
      if (!inter) return;
      e.preventDefault();

      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const cx = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
      const cy = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
      const pos = { x: cx - rect.left, y: cy - rect.top };
      
      const dx = pos.x - inter.startPos.x;
      const dy = pos.y - inter.startPos.y;
      const sb = inter.startBox;

      let newBox;

      switch (inter.action) {
        case 'move':
          newBox = { x: sb.x + dx, y: sb.y + dy, w: sb.w, h: sb.h };
          break;
        case 'resize': {
          newBox = { ...sb };
          const h = inter.handle;
          if (h.includes('e')) newBox.w = sb.w + dx;
          if (h.includes('w')) { newBox.x = sb.x + dx; newBox.w = sb.w - dx; }
          if (h.includes('s')) newBox.h = sb.h + dy;
          if (h.includes('n')) { newBox.y = sb.y + dy; newBox.h = sb.h - dy; }
          break;
        }
        case 'new': {
          const anchor = inter.anchorPos || inter.startPos;
          newBox = {
            x: Math.min(anchor.x, pos.x),
            y: Math.min(anchor.y, pos.y),
            w: Math.abs(pos.x - anchor.x),
            h: Math.abs(pos.y - anchor.y),
          };
          break;
        }
        default:
          return;
      }

      setBox(clampBox(newBox));
    };

    const handleDocUp = () => {
      interactionRef.current = null;
    };

    // Passive: false für preventDefault bei Touch
    document.addEventListener('pointermove', handleDocMove, { passive: false });
    document.addEventListener('pointerup', handleDocUp);
    document.addEventListener('pointercancel', handleDocUp);
    
    return () => {
      document.removeEventListener('pointermove', handleDocMove);
      document.removeEventListener('pointerup', handleDocUp);
      document.removeEventListener('pointercancel', handleDocUp);
    };
  }, [clampBox]);

  // ── Bestätigen ──
  // Direkt berechneter Wert (kein useCallback) — verhindert Stale-Closure-Probleme
  const boxValid = (() => {
    if (!box) return false;
    if (box.w < MIN_BOX_SIZE || box.h < MIN_BOX_SIZE) return false;
    const orig = toOriginal(box);
    if (orig.w < MIN_ORIGINAL_SIZE || orig.h < MIN_ORIGINAL_SIZE) return false;
    return true;
  })();

  // Debug-Info für Box-Status (wird auf dem Bildschirm angezeigt)
  const debugInfo = box 
    ? `Display: ${Math.round(box.w)}×${Math.round(box.h)} | Original: ${Math.round(box.w * scale.x)}×${Math.round(box.h * scale.y)} | ${boxValid ? '✅' : '❌'}`
    : 'Keine Box';
  
  const handleConfirm = useCallback(() => {
    console.log('📐 handleConfirm aufgerufen, box:', box, 'boxValid:', boxValid);
    if (!box) {
      console.log('📐 handleConfirm: Keine Box vorhanden');
      return;
    }
    // Bestätige auch bei "ungültiger" Box — robuster als disabled-Button
    const origBox = toOriginal(box);
    console.log('📐 handleConfirm: Bestätige mit Original-Box:', origBox);
    onConfirm(origBox);
  }, [box, boxValid, toOriginal, onConfirm]);

  // ── ESC zum Abbrechen ──
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  // Farben
  const bg = colors?.darkMode ? '#111' : '#000';
  const textColor = '#fff';

  // Portal: Rendert auf document.body, damit der RegionSelector aus dem
  // Stacking Context des NewPackageModal (zIndex:1000) ausbricht und
  // ÜBER der Tab-Bar liegt.
  return createPortal(
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      zIndex: 99999,
      background: bg,
      display: 'flex',
      flexDirection: 'column',
      height: '100dvh',
    }}>
      {/* ── Header (kompakt) ── */}
      <div style={{ 
        padding: '8px 16px 4px', 
        textAlign: 'center',
        flexShrink: 0,
      }}>
        <div style={{ color: textColor, fontSize: 14, fontWeight: 'bold' }}>
          📐 {t('ocr.correctRecipientRegionTitle')}
        </div>
        <div style={{ color: '#777', fontSize: 11 }}>
          {t('ocr.regionInstructions')}
        </div>
      </div>

      {/* ── Bild + Auswahl (flex:1 füllt den verfügbaren Platz) ── */}
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        padding: '4px 8px',
        overflow: 'hidden',
        minHeight: 0,
      }}>
        <div
          ref={containerRef}
          style={{
            position: 'relative',
            display: 'inline-block',
            maxWidth: '100%',
            maxHeight: '100%',
            touchAction: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            lineHeight: 0,
          }}
          onPointerDown={handlePointerDown}
        >
          <img
            ref={imgRef}
            src={imageUrl}
            alt="Originalbild"
            onLoad={handleImageLoad}
            style={{
              maxWidth: '100%',
              maxHeight: 'calc(100dvh - 130px)',
              display: 'block',
              borderRadius: 6,
            }}
            draggable={false}
          />

          {/* Auswahl-Rahmen mit dunklem Overlay */}
          {box && imgLoaded && (
            <>
              {/* Dunkles Overlay (4 Bereiche um die Auswahl) */}
              <div style={{
                position: 'absolute', top: 0, left: 0,
                width: '100%', height: Math.max(0, box.y),
                background: 'rgba(0,0,0,0.55)', pointerEvents: 'none',
              }} />
              <div style={{
                position: 'absolute', top: box.y, left: 0,
                width: Math.max(0, box.x), height: box.h,
                background: 'rgba(0,0,0,0.55)', pointerEvents: 'none',
              }} />
              <div style={{
                position: 'absolute', top: box.y, left: box.x + box.w,
                right: 0, height: box.h,
                background: 'rgba(0,0,0,0.55)', pointerEvents: 'none',
              }} />
              <div style={{
                position: 'absolute', top: box.y + box.h, left: 0,
                width: '100%', bottom: 0,
                background: 'rgba(0,0,0,0.55)', pointerEvents: 'none',
              }} />

              {/* Rahmen */}
              <div style={{
                position: 'absolute',
                left: box.x,
                top: box.y,
                width: box.w,
                height: box.h,
                border: `2.5px solid ${ACCENT}`,
                pointerEvents: 'none',
                zIndex: 2,
              }}>
                <div style={{
                  position: 'absolute', left: '33.3%', top: 0, width: 1, height: '100%',
                  background: 'rgba(255,255,255,0.2)', pointerEvents: 'none',
                }} />
                <div style={{
                  position: 'absolute', left: '66.6%', top: 0, width: 1, height: '100%',
                  background: 'rgba(255,255,255,0.2)', pointerEvents: 'none',
                }} />
                <div style={{
                  position: 'absolute', top: '33.3%', left: 0, height: 1, width: '100%',
                  background: 'rgba(255,255,255,0.2)', pointerEvents: 'none',
                }} />
                <div style={{
                  position: 'absolute', top: '66.6%', left: 0, height: 1, width: '100%',
                  background: 'rgba(255,255,255,0.2)', pointerEvents: 'none',
                }} />
              </div>

              {/* Eck-Handles */}
              {['nw', 'ne', 'sw', 'se'].map(corner => (
                <div
                  key={corner}
                  style={{
                    position: 'absolute',
                    width: HANDLE_RADIUS * 2,
                    height: HANDLE_RADIUS * 2,
                    background: ACCENT,
                    border: '2px solid #fff',
                    borderRadius: '50%',
                    transform: 'translate(-50%, -50%)',
                    left: box.x + (corner.includes('e') ? box.w : 0),
                    top: box.y + (corner.includes('s') ? box.h : 0),
                    pointerEvents: 'none',
                    zIndex: 3,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.5)',
                  }}
                />
              ))}

              {/* Größen-Anzeige */}
              <div style={{
                position: 'absolute',
                left: box.x + box.w / 2,
                top: box.y + box.h + 8,
                transform: 'translateX(-50%)',
                color: ACCENT,
                fontSize: 11,
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                zIndex: 3,
                textShadow: '0 1px 3px rgba(0,0,0,0.8)',
              }}>
                {Math.round(box.w * scale.x)} × {Math.round(box.h * scale.y)} px
              </div>
            </>
          )}

          {/* Andere erkannte Regionen (read-only Overlays) */}
          {allRegions && imgLoaded && allRegions
            .filter(r => r.type !== 'empfaenger')
            .map((region, i) => {
              const rc = REGION_COLORS[region.type] || { color: '#999', label: region.type };
              const rx = region.box.x / scale.x;
              const ry = region.box.y / scale.y;
              const rw = region.box.w / scale.x;
              const rh = region.box.h / scale.y;
              return (
                <div key={`ro-${i}`} style={{
                  position: 'absolute',
                  left: rx, top: ry, width: rw, height: rh,
                  border: `2px dashed ${rc.color}`,
                  background: `${rc.color}15`,
                  pointerEvents: 'none',
                  zIndex: 4,
                  borderRadius: 3,
                }}>
                  <span style={{
                    position: 'absolute', top: -18, left: 0,
                    fontSize: 10, color: '#fff', background: rc.color,
                    padding: '1px 6px', borderRadius: 3, whiteSpace: 'nowrap',
                    lineHeight: '16px', fontWeight: 'bold', opacity: 0.9,
                  }}>
                    {rc.labelKey ? t(rc.labelKey) : rc.label} {Math.round(region.confidence * 100)}%
                  </span>
                </div>
              );
            })}

          {/* Lade-Overlay */}
          {loading && (
            <div style={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.7)',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              zIndex: 10, borderRadius: 6,
            }}>
              <div style={{ fontSize: 40, marginBottom: 8, animation: 'regionPulse 1.2s ease-in-out infinite' }}>🔍</div>
              <div style={{ color: '#fff', fontSize: 14, fontWeight: 'bold' }}>{t('ocr.analyzingRegion')}</div>
              <style>{`@keyframes regionPulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.2); } }`}</style>
            </div>
          )}
        </div>
      </div>

      {/* ── Buttons (eigener Layer, garantiert klickbar) ── */}
      <div 
        style={{
          flexShrink: 0,
          display: 'flex',
          gap: 12,
          justifyContent: 'center',
          padding: '10px 16px',
          paddingBottom: 'max(14px, env(safe-area-inset-bottom, 14px))',
          background: '#1a1a1a',
          borderTop: '1px solid #333',
          position: 'relative',
          zIndex: 100,
          pointerEvents: 'auto',
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
      >
        <button
          onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); onCancel(); }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCancel(); }}
          onPointerDown={(e) => e.stopPropagation()}
          disabled={loading}
          style={{
            padding: '14px 24px',
            fontSize: 16,
            borderRadius: 12,
            border: '2px solid #666',
            background: '#333',
            color: '#fff',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.5 : 1,
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            pointerEvents: 'auto',
            minWidth: 120,
          }}
        >
          ✕ {t('common.cancel')}
        </button>
        <button
          onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); handleConfirm(); }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleConfirm(); }}
          onPointerDown={(e) => e.stopPropagation()}
          disabled={loading || !box}
          style={{
            padding: '14px 28px',
            fontSize: 16,
            fontWeight: 'bold',
            borderRadius: 12,
            border: '2px solid ' + ((!box || loading) ? '#555' : ACCENT),
            background: (!box || loading) ? '#555' : ACCENT,
            color: '#fff',
            cursor: (!box || loading) ? 'not-allowed' : 'pointer',
            opacity: (!box || loading) ? 0.5 : 1,
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            pointerEvents: 'auto',
            minWidth: 160,
            boxShadow: box && !loading ? `0 4px 20px ${ACCENT}AA` : 'none',
          }}
        >
          {loading ? `⏳ ${t('ocr.analyzingShort')}` : `✅ ${t('common.confirm')}`}
        </button>
      </div>
    </div>,
    document.body
  );
}
